import { useEffect } from 'react';

import { ClothesPile } from '@depop/web-thrift-ui/Illustrations/ClothesPile';
import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import { Divider } from '@/components/OrDivider';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useExperimentWithVariables } from '@/modules/experiments/hooks/useExperimentWithVariables';
import { type QuizAnswers } from '@/modules/quiz/types';
import { getResultsURL } from '@/modules/quiz/utils';

export function QuizSignupGate({ answers }: { answers: QuizAnswers }) {
  const t = useTranslations('quiz');
  const { title, description } = useExperimentWithVariables(
    'web_9149_style_quiz_signup_gate',
    {
      title: t('SignupGate.Title'),
      description: t('SignupGate.Description'),
    }
  );
  const { sendActivityTrackerEvent } = useActivityTracker();
  const redirectParam = encodeURIComponent(getResultsURL(answers));

  useEffect(() => {
    sendActivityTrackerEvent(ActivityTrackerEventType.STYLE_QUIZ_ACTION, {
      type: 'signup_view',
      category: answers.category,
      style: answers.style,
      colours: answers.colors,
      step: 4,
      schemaVersion: '1.0',
    });
  }, []);

  function handleSignupClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: 'style_quiz_interstitial_signup',
      schemaVersion: '1.0',
    });
  }

  function handleSkipClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.STYLE_QUIZ_ACTION, {
      type: 'signup_skip',
      category: answers.category,
      style: answers.style,
      colours: answers.colors,
      step: 4,
      schemaVersion: '1.0',
    });
  }

  return (
    <div className={styles.signupGateContainer}>
      <ClothesPile size="large" />
      <div className={styles.signupGateContent}>
        <Heading variant="lg">{title}</Heading>
        <Body variant="md-regular">{description}</Body>
      </div>
      <div className={styles.signupGateContent}>
        <ExtendedLink
          href={`/signup/?redirect=${redirectParam}`}
          onClick={handleSignupClick}
        >
          <ButtonExperiment as="span" fullWidth>
            {t('SignupGate.SignUp')}
          </ButtonExperiment>
        </ExtendedLink>
        <Body>
          {t.rich('SignupGate.Login', {
            login: (children) => (
              <ExtendedLink href={`/login/?redirect=${redirectParam}`}>
                <Body as="span" variant="sm-bold" className={styles.loginLink}>
                  {children}
                </Body>
              </ExtendedLink>
            ),
          })}
        </Body>
        <Divider className={styles.quizSignupDivider} />
        <ExtendedLink href={getResultsURL(answers)} onClick={handleSkipClick}>
          <ButtonExperiment variant="secondary" fullWidth as="span">
            {t('SignupGate.SkipToResults')}
          </ButtonExperiment>
        </ExtendedLink>
      </div>
    </div>
  );
}
